import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@src/common/components";
import { incrementDays, isSameDay } from "@src/common/lib/DateUtils";
import { DatePicker } from "baseui/datepicker";
import {
  Theme,
  useTheme,
} from "@src/common/components/ThemeProvider/ThemeProvider";
import {
  getCourierTodayForDatePicker,
  isCourierToday,
} from "@src/common/utils/timezoneConverters";

type Props = {
  minDate: Date | undefined;
  setMinDate: (date: Date) => void;
  maxDate: Date | undefined;
  setMaxDate: (date: Date) => void;
};

export const DateBasedPaginator = function ({
  minDate,
  setMinDate,
  maxDate,
  setMaxDate,
}: Props) {
  const { effectiveTheme } = useTheme();
  const sameDay = isSameDay(minDate, maxDate);

  const isTodaySelected = sameDay && isCourierToday(minDate);

  const isDark = effectiveTheme === Theme.DARK;
  return (
    <div className="flex items-end gap-1">
      <div className="flex items-center">
        {!isTodaySelected && (
          <div>
            <Button
              size={"xs"}
              color={"gray"}
              onClick={() => {
                setMinDate(getCourierTodayForDatePicker());
                setMaxDate(getCourierTodayForDatePicker());
              }}
              className={"hover:bg-slate-100 dark:hover:bg-gray-700"}
            >
              <div className="flex gap-1 items-center truncate">
                <CalendarIcon className="h-4 w-4" /> <span>Go to Today</span>
              </div>
            </Button>
          </div>
        )}
        <div className="flex flex-1">
          <Button
            size={"xs"}
            color={"gray"}
            onClick={() => {
              if (!minDate || !maxDate) {
                return;
              }
              setMinDate(incrementDays(minDate, -1));
              setMaxDate(incrementDays(maxDate, -1));
            }}
            className="hover:bg-slate-100 dark:hover:bg-gray-700"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
          <Button
            size={"xs"}
            color={"gray"}
            onClick={() => {
              if (!minDate || !maxDate) {
                return;
              }
              setMinDate(incrementDays(minDate, 1));
              setMaxDate(incrementDays(maxDate, 1));
            }}
            className="hover:bg-slate-100 dark:hover:bg-gray-700"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>
      <div className="flex-init">
        <DatePicker
          range={true}
          value={sameDay ? [minDate, minDate] : [minDate, maxDate]}
          formatString={"MM/dd/yy"}
          size="mini"
          onChange={({ date }) => {
            setMinDate(date[0]);
            setMaxDate(date[1]);
          }}
          quickSelect
          overrides={{
            Input: {
              props: {
                overrides: {
                  Input: {
                    style: {
                      cursor: "pointer",
                      backgroundColor: isDark
                        ? "rgb(31 41 55)"
                        : "rgb(255 255 255)",
                      color: isDark ? "rgb(209 213 219)" : undefined,
                      borderBottomRightRadius: "6px",
                      ":hover": {
                        backgroundColor: isDark
                          ? "rgb(55 65 81)"
                          : "rgb(249 250 251)",
                      },
                      ":focus": {
                        outline: "none",
                        boxShadow: "none",
                        backgroundColor: isDark
                          ? "rgb(55 65 81)"
                          : "rgb(249 250 251)",
                      },
                    },
                  },
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};
